import { useEffect, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Select } from '../../components/atoms/Select'
import { Field } from '../../components/molecules/Field'
import {
  ORDERSTATUS,
  ORDERSTATUSLABEL,
  type OrderStatus
} from '../../../constants/order-status.constants'
import type { WebhookProviderDto } from '../../../viewmodels/useWebhookProviders'

const SLUG_PATTERN = /^[a-z0-9-]{1,64}$/

interface EventRow {
  readonly key: string                  // local React key
  eventName: string
  status: OrderStatus
}

interface Props {
  readonly open: boolean
  readonly initial: WebhookProviderDto | null  // null = create-new
  readonly onClose: () => void
  readonly onSubmit: (input: {
    provider: string
    display_name: string
    events: Record<string, OrderStatus>
  }) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
}

export function WebhookProviderEditModal({ open, initial, onClose, onSubmit }: Props): JSX.Element {
  const [provider, setProvider] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [rows, setRows] = useState<EventRow[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [field, setField] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (initial === null) {
      setProvider('')
      setDisplayName('')
      setRows([])
    } else {
      setProvider(initial.provider)
      setDisplayName(initial.display_name)
      setRows(
        Object.entries(initial.events).map(([k, v], i) => ({
          key: `r-${i}-${k}`,
          eventName: k,
          status: v
        }))
      )
    }
    setError(null)
    setField(null)
    setSubmitting(false)
  }, [open, initial?.provider])

  const addRow = (): void => {
    setRows((s) => [
      ...s,
      { key: `r-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, eventName: '', status: ORDERSTATUS.DELIVERED }
    ])
  }
  const updateRow = (key: string, patch: Partial<Omit<EventRow, 'key'>>): void => {
    setRows((s) => s.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }
  const removeRow = (key: string): void => {
    setRows((s) => s.filter((r) => r.key !== key))
  }

  const handleSubmit = async (): Promise<void> => {
    setError(null)
    setField(null)
    const trimmedSlug = provider.trim().toLowerCase()
    if (initial === null && !SLUG_PATTERN.test(trimmedSlug)) {
      setField('provider')
      setError('provider slug must be 1–64 lowercase alphanumeric or hyphen chars')
      return
    }
    const trimmedName = displayName.trim()
    if (trimmedName.length === 0) {
      setField('display_name')
      setError('display_name is required')
      return
    }
    const events: Record<string, OrderStatus> = {}
    for (const r of rows) {
      const evtName = r.eventName.trim()
      if (evtName.length === 0) continue          // silently skip blank rows
      if (events[evtName] !== undefined) {
        setField('events')
        setError(`duplicate event name: ${evtName}`)
        return
      }
      events[evtName] = r.status
    }

    setSubmitting(true)
    const res = await onSubmit({
      provider: initial === null ? trimmedSlug : initial.provider,
      display_name: trimmedName,
      events
    })
    setSubmitting(false)
    if (!res.ok) {
      setField(res.field)
      setError(res.message)
    } else {
      onClose()
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial === null ? 'Add webhook provider' : `Edit ${initial.display_name}`}
      maxWidth="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error !== null ? <Alert tone="error">{error}</Alert> : null}

        <Field
          label="Provider slug"
          helper="Lowercase alphanumeric + hyphen. Used in the webhook URL (/webhooks/<slug>). Locked after create."
          error={field === 'provider' ? error : null}
        >
          <Input
            value={provider}
            disabled={initial !== null}
            onChange={(e) => setProvider(e.target.value)}
            placeholder="e.g. shiprocket"
            hasError={field === 'provider'}
          />
        </Field>

        <Field
          label="Display name"
          error={field === 'display_name' ? error : null}
        >
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Shiprocket"
            hasError={field === 'display_name'}
            maxLength={128}
          />
        </Field>

        <fieldset className="space-y-3 rounded-lg border border-border bg-bg-primary/40 p-4">
          <legend className="px-1 font-body text-body-sm font-semibold text-charcoal">
            Event mappings
          </legend>
          <p className="font-body text-body-xs text-text-secondary">
            Map the provider&apos;s incoming event name (e.g. <span className="font-mono">parcel.delivered</span>) to one of our OrderStatus enum values.
          </p>
          {rows.length === 0 ? (
            <p className="text-text-secondary text-body-sm py-2 text-center">
              No mappings yet — add one below.
            </p>
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.key} className="flex items-center gap-2">
                  <Input
                    value={r.eventName}
                    onChange={(e) => updateRow(r.key, { eventName: e.target.value })}
                    placeholder="event name"
                    className="flex-1"
                    maxLength={128}
                  />
                  <span className="text-text-secondary">→</span>
                  <Select
                    value={r.status}
                    onChange={(e) => updateRow(r.key, { status: e.target.value as OrderStatus })}
                    className="w-56"
                  >
                    {Object.values(ORDERSTATUS).map((s) => (
                      <option key={s} value={s}>
                        {ORDERSTATUSLABEL[s as OrderStatus]} ({s})
                      </option>
                    ))}
                  </Select>
                  <button
                    type="button"
                    className="text-body-xs text-rust hover:text-charcoal underline-offset-2 hover:underline"
                    onClick={() => removeRow(r.key)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          <Button size="sm" variant="secondary" onClick={addRow}>
            Add mapping
          </Button>
        </fieldset>
      </div>
    </Modal>
  )
}
